import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Section, Field, ActionBar } from "@/components/form-shell";

const EMPTY = {
  name: "", website: "", description: "",
  strengths: "", weaknesses: "",
  ourAdvantages: "", ourDisadvantages: "",
  threatLevel: "MEDIUM", isActive: true, tags: "",
};
type Form = typeof EMPTY;

export default function CompetitorNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);

  function set<K extends keyof Form>(k: K, v: Form[K]) { setForm((f) => ({ ...f, [k]: v })); }

  async function save(mode: "save" | "saveAndNew") {
    setBusy(true); setError(null);
    try {
      const payload: any = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === "" || v === null || v === undefined) continue;
        if (k === "tags") payload.tags = (v as string).split(",").map((s) => s.trim()).filter(Boolean);
        else payload[k] = v;
      }
      await api.post("/competitors", payload);
      qc.invalidateQueries({ queryKey: ["/competitors"] });
      if (mode === "saveAndNew") setForm(EMPTY);
      else navigate("/competitors");
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Failed to create competitor.");
    } finally { setBusy(false); }
  }

  return (
    <>
      <PageHeader title="Create Competitor" description="Track a third-party company you compete against." />
      <form onSubmit={(e) => { e.preventDefault(); save("save"); }} className="p-6">
        <div className="mx-auto max-w-3xl space-y-4">
          <Section title="Identity">
            <Field id="name" label="Name" required className="md:col-span-2">
              <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)} />
            </Field>
            <Field id="website" label="Website">
              <Input id="website" type="url" placeholder="https://…" value={form.website} onChange={(e) => set("website", e.target.value)} />
            </Field>
            <Field id="threatLevel" label="Threat level">
              <Select id="threatLevel" value={form.threatLevel} onChange={(e) => set("threatLevel", e.target.value)}>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </Select>
            </Field>
            <div className="flex items-center gap-2 md:col-span-2">
              <input id="isActive" type="checkbox" checked={form.isActive} onChange={(e) => set("isActive", e.target.checked)} />
              <label htmlFor="isActive" className="text-sm font-medium">Active competitor (shows up in pickers)</label>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="tags" className="text-sm font-medium">Tags (comma-separated)</label>
              <Input id="tags" placeholder="enterprise, displacement-target" value={form.tags} onChange={(e) => set("tags", e.target.value)} />
            </div>
          </Section>

          <Section title="Profile">
            <div className="md:col-span-2">
              <label htmlFor="description" className="text-sm font-medium">Description</label>
              <textarea
                id="description"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="strengths" className="text-sm font-medium">Their strengths</label>
              <textarea
                id="strengths"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.strengths}
                onChange={(e) => set("strengths", e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="weaknesses" className="text-sm font-medium">Their weaknesses</label>
              <textarea
                id="weaknesses"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.weaknesses}
                onChange={(e) => set("weaknesses", e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="ourAdvantages" className="text-sm font-medium">Our advantages vs them</label>
              <textarea
                id="ourAdvantages"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.ourAdvantages}
                onChange={(e) => set("ourAdvantages", e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="ourDisadvantages" className="text-sm font-medium">Our disadvantages vs them</label>
              <textarea
                id="ourDisadvantages"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.ourDisadvantages}
                onChange={(e) => set("ourDisadvantages", e.target.value)}
              />
            </div>
          </Section>

          <ActionBar
            busy={busy}
            canSubmit={!!form.name}
            onCancel={() => navigate("/competitors")}
            onSaveAndNew={() => save("saveAndNew")}
            onSubmit={() => save("save")}
            error={error}
          />
        </div>
      </form>
    </>
  );
}
